import { useMemo } from 'react';

import { useAtom } from 'jotai';

import { getRevenueFromResponseCurve } from '../../MarketingMixModeling/forecast/forecastData';
import { trainingStatsWithSpend, calcAverageBaselineFromSeasonality } from '../../MarketingMixModeling/forecast/forecastHelpers';
import { buildRecommendation } from '../../MarketingMixModeling/hooks/buildRecommendation';
import { alignDateToPreviousYear } from '../../MarketingMixModeling/parts/time';
import { startDateBaselineAtom, endDateBaselineAtom } from '../atoms';
import { useProjectionPeriod } from '../ScenarioForm/hooks/usePeriods';

const getBaselineRecommendation = (responseCurves, trainingStats, constraints, featureStats) => {
  if (!responseCurves || !trainingStats || !constraints || !featureStats) return {};

  const stats = trainingStatsWithSpend(trainingStats, featureStats);

  const recommendation = buildRecommendation(responseCurves, stats, constraints);

  if (!recommendation) return {};
  return recommendation;
};

const addCurrentRevenueToBaseline = (baselineRecommendation, responseCurves) => {
  if (!baselineRecommendation || !Object.keys(baselineRecommendation).length || !responseCurves) {
    return baselineRecommendation;
  }

  return Object.keys(baselineRecommendation).reduce((accu, channel) => {
    const currentRevenue = getRevenueFromResponseCurve(
      responseCurves[channel],
      baselineRecommendation[channel].currentSpend,
    );

    accu[channel] = {
      ...baselineRecommendation[channel],
      currentRevenue,
    };
    return accu;
  }, {});
};

export const useBaselineData = ({
  trainingStats,
  constraints,
  responseCurves,
  featureStats,
}) => {
  // Note: we build the baseline recommendation, but it is primarily used to calculate the baseline spend for channels
  // We recompute the baseline projections in the optimization step based on the "current" spend from the baseline
  // Not entirely sure why we need to do this, but it is what it is?
  // Ideally, we should unpack the buildRecommendation function so that it only supplies the necessary information we need

  // TODO: refactor buildRecommendation to only supply the necessary information.
  // Dont actually produce a forecast at this step
  const baseline = useMemo(() => {
    const baselineRecommendation = getBaselineRecommendation(responseCurves, trainingStats, constraints, featureStats);
    const baselineWithCurrentRevenue = addCurrentRevenueToBaseline(baselineRecommendation, responseCurves);
    return baselineWithCurrentRevenue;
  }, [trainingStats, constraints, responseCurves, featureStats]);

  return {
    baseline,
  };
};

export const useOrganicBaselineRevenue = ({ seasonality }) => {
  const [startDate] = useAtom(startDateBaselineAtom);
  const [endDate] = useAtom(endDateBaselineAtom);

  const { projectionPeriod } = useProjectionPeriod();

  const organicBaselineRevenue = useMemo(() => {
    if (!seasonality?.length) return 0;
    const lastDate = seasonality[seasonality.length - 1].date;

    const adjustedStartDate = alignDateToPreviousYear(startDate, lastDate);
    const adjustedEndDate = alignDateToPreviousYear(endDate, lastDate);

    return calcAverageBaselineFromSeasonality(
      seasonality,
      projectionPeriod,
      adjustedStartDate,
      adjustedEndDate,
    );
  }, [seasonality, projectionPeriod, startDate, endDate]);

  return {
    organicBaselineRevenue,
    organicBaselineRevenuePerDay: organicBaselineRevenue / projectionPeriod,
  };
};
